<script setup>
/**
 * Практика по теме услуги: реальные дела из нашей базы решений.
 *
 * ⚠ БЫЛА ГАЛЕРЕЯ КАРТИНОК. Блок показывал обложки статей-историй: три четверти
 * площади занимала картинка, а сути дела в ней не было — ни суда, ни номера,
 * ни чем кончилось. Проверить такую «практику» читатель не мог. Теперь здесь
 * дела, которые можно сверить в картотеке: суд, номер, дата, исход.
 *
 * Разбор юриста и просто решение суда помечены по-разному: первое — наша
 * работа, второе — общедоступный акт, и выдавать одно за другое нельзя.
 */
import { ref, computed } from 'vue';

const props = defineProps({
  cases: { type: Array, default: () => [] },
  // Статьи-истории юриста из его практики: header, description, url
  articles: { type: Array, default: () => [] },
  keyword: { type: String, default: '' },
});

// 12 карточек подряд — два экрана прокрутки на телефоне. Показываем первые
// четыре, остальное по кнопке: кто хочет читать, откроет.
const SHOWN = 4;
const allArticles = ref(false);
const visibleArticles = computed(() =>
  allArticles.value ? props.articles : props.articles.slice(0, SHOWN));
</script>

<template>
  <section v-if="cases.length || articles.length" class="w-full">
    <div class="max-w-5xl mx-auto px-4 2xl:px-0">
      <h2 class="text-2xl font-bold tracking-tight text-gray-900">
        {{ keyword }} — практика
      </h2>

      <!--
        Статьи юриста — первыми: это его собственные дела, рассказанные им самим.
        ⚠ БЕЗ ОБЛОЖЕК. Раньше три четверти карточки занимала картинка, а сути не
        было видно; теперь заголовок и первая мысль статьи.
      -->
      <template v-if="articles.length">
        <h3 class="mt-6 text-lg font-semibold text-gray-900">Из практики юриста</h3>
        <ol class="mt-4 grid gap-4 md:grid-cols-2">
          <li v-for="a in visibleArticles" :key="'a' + a.id"
              class="rounded-xl border border-gray-200 bg-white p-4 md:p-6">
            <div class="flex flex-wrap items-center gap-2">
              <span class="rounded-lg bg-indigo-50 px-2 py-1 text-xs font-semibold text-indigo-700">Статья юриста</span>
              <span v-if="a.year" class="text-xs text-gray-400">{{ a.year }}</span>
            </div>
            <h4 class="mt-3 text-lg font-semibold leading-tight text-gray-900">
              <a :href="route('articles/url', a.url)" class="hover:text-indigo-700">{{ a.header }}</a>
            </h4>
            <p v-if="a.description" class="mt-2 text-sm leading-relaxed text-gray-600 line-clamp-3">
              {{ a.description }}
            </p>
            <a :href="route('articles/url', a.url)"
               class="mt-3 inline-block text-sm font-medium text-indigo-600 hover:underline">
              Читать
            </a>
          </li>
        </ol>
        <button v-if="articles.length > SHOWN && !allArticles" type="button"
                class="mt-4 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:border-indigo-300 hover:text-indigo-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-indigo-600"
                @click="allArticles = true">
          Показать ещё {{ articles.length - SHOWN }}
        </button>
      </template>

      <template v-if="cases.length">
        <h3 class="mt-8 text-lg font-semibold text-gray-900">Решения судов</h3>
        <p class="mt-2 text-gray-500 leading-relaxed">
          Судебные акты по делам данной категории с указанием наименования суда,
          номера дела и даты вынесения. Тексты приведены в обезличенном виде.
        </p>

      <ol class="mt-4 grid gap-4 md:grid-cols-2">
        <li v-for="c in cases" :key="c.id"
            class="rounded-xl border border-gray-200 bg-white p-4 md:p-6">
          <div class="flex flex-wrap items-center gap-2">
            <span class="rounded-lg px-2 py-1 text-xs font-semibold"
                  :class="c.kind === 'analysis'
                    ? 'bg-indigo-50 text-indigo-700'
                    : 'bg-gray-100 text-gray-600'">
              {{ c.kind === 'analysis' ? 'Разбор юриста' : 'Решение суда' }}
            </span>
            <span v-if="c.actDate" class="text-xs text-gray-400">{{ c.actDate }}</span>
          </div>

          <h3 class="mt-3 text-lg font-semibold leading-tight text-gray-900">
            <a :href="c.url" class="hover:text-indigo-700">{{ c.title }}</a>
          </h3>

          <p v-if="c.excerpt" class="mt-2 text-sm leading-relaxed text-gray-600 line-clamp-3">
            {{ c.excerpt }}
          </p>

          <p class="mt-3 text-xs leading-relaxed text-gray-400">
            {{ c.court }}<template v-if="c.number"> · <span class="whitespace-nowrap">дело № {{ c.number }}</span></template>
          </p>

          <div class="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
            <a :href="c.url" class="text-sm font-medium text-indigo-600 hover:underline">
              Текст решения
            </a>
            <!-- Кто разбирал: имя юриста здесь — подпись под работой, а не реклама -->
            <a v-for="l in c.lawyers" :key="l.url" :href="l.url"
               class="text-xs text-gray-500 hover:text-indigo-700">
              {{ l.name }}
            </a>
          </div>
        </li>
      </ol>
      </template>
    </div>
  </section>
</template>
